import { useEffect } from 'react';

import type { IEventBus } from '../../../core/events/IEventBus';
import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import { useCommentOverlayStore } from '../../../stores/commentOverlayStore';
import type { CommentAxisAnchor, CommentShapeLocation } from '../../../domains/comments/models/Comment';

export function useCanvasCommentPlacementBridge(input: {
  eventBus: IEventBus;
  toolRegistry: ToolRegistry | null | undefined;
}): void {
  const { eventBus, toolRegistry } = input;

  // Bridge tool-emitted comment placement events to the overlay prompt lifecycle.
  useEffect(() => {
    const openHandler = (payload: unknown) => {
      const data = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
      const diagramRaw = data.diagram && typeof data.diagram === 'object' ? (data.diagram as Record<string, unknown>) : {};
      const localRaw = data.local && typeof data.local === 'object' ? (data.local as Record<string, unknown>) : {};

      const shapeId = typeof data.shapeId === 'string' ? data.shapeId : null;
      let locOnShape: CommentShapeLocation | null = null;
      if (data.locOnShape && typeof data.locOnShape === 'object') {
        const loc = data.locOnShape as Record<string, unknown>;
        const parseAxisAnchor = (raw: unknown): CommentAxisAnchor | null => {
          if (!raw || typeof raw !== 'object') return null;
          const r = raw as Record<string, unknown>;
          const type = r.type;
          const value = r.value;
          if (
            (type === 'relative' || type === 'fixed-start' || type === 'fixed-end') &&
            typeof value === 'number'
          ) {
            return { type, value };
          }
          return null;
        };

        const x = parseAxisAnchor(loc.x);
        const y = parseAxisAnchor(loc.y);
        if (x && y) locOnShape = { x, y };
      }

      useCommentOverlayStore.getState().openPrompt({
        diagram: {
          x: typeof diagramRaw.x === 'number' ? diagramRaw.x : 0,
          y: typeof diagramRaw.y === 'number' ? diagramRaw.y : 0,
        },
        local: {
          x: typeof localRaw.x === 'number' ? localRaw.x : 0,
          y: typeof localRaw.y === 'number' ? localRaw.y : 0,
        },
        shapeId,
        locOnShape,
      });
    };

    const closeHandler = () => {
      useCommentOverlayStore.getState().closePrompt();
    };

    const completedHandler = () => {
      useCommentOverlayStore.getState().closePrompt();
      // Directly activate selection tool synchronously to avoid race conditions and delays
      // The CommentsTool's resetPlacementState will be called via event listener,
      // and then the tool will be deactivated when we activate the selection tool
      if (toolRegistry) {
        const activated = toolRegistry.activateTool('genogram-selection');
        if (!activated) {
          // Fallback to event-based activation if direct call fails
          eventBus.emit('tool:activate', { id: 'genogram-selection' });
        }
      } else {
        // Fallback if toolRegistry not yet available
        eventBus.emit('tool:activate', { id: 'genogram-selection' });
      }
    };

    const unsubscribeOpen = eventBus.on('comment:placement:open', openHandler);
    const unsubscribeCompleted = eventBus.on('comment:placement:completed', completedHandler);
    const unsubscribeCancelled = eventBus.on('comment:placement:cancelled', closeHandler);

    // Allow tools to request activation by id (e.g., selection after a one-shot tool)
    const unsubscribeToolActivate = eventBus.on('tool:activate', (payload: unknown) => {
      try {
        const maybe = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
        const id =
          (typeof maybe.id === 'string' ? maybe.id : undefined) ??
          (typeof maybe.toolId === 'string' ? maybe.toolId : undefined);
        if (id && toolRegistry) {
          toolRegistry.activateTool(id);
        }
      } catch {}
    });

    return () => {
      try { unsubscribeOpen?.(); } catch {}
      try { unsubscribeCompleted?.(); } catch {}
      try { unsubscribeCancelled?.(); } catch {}
      try { unsubscribeToolActivate?.(); } catch {}
    };
  }, [eventBus, toolRegistry]);
}
